import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { colors } from '@united-talent-agency/julius-frontend-components';
import { loadLiteraryNotes, removeProjectNote, updateProjectNote } from '@united-talent-agency/julius-frontend-store';
import { withStyles } from '@material-ui/core/styles';

import NoteTableActionHeader from '../note-table-action-header';
import Note from '../note';
import ProjectNoteForm from '../project-note-form';
import { notify } from 'react-notify-toast';
import DataTableLoader from '../../../../../../components/content-loader/content-loader';
import SectionError from '../../../../../../components/section-error/section-error';

const HEADERS = ['Person', 'Date', 'Client', 'Status', 'Note', 'Internal', 'Action'];

const LiteraryNotes = ({ project, classes, retryToken = 0, isRetrying = false }) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [isEdit, setIsEditing] = useState(false);
  const [focused, setFocused] = useState(0);
  const [note, setNote] = useState({});
  // Case 2 local flag — set when the view's own loadLiteraryNotes
  // dispatch comes back !ok (500, blocked URL, parseError, network error).
  // Needed because the library reducer blindly writes action.body into
  // state.projectNotes.literaryNotes, which on failure would be a
  // `{ error }` envelope — `.filter(...)` on it crashes.
  const [hasLoadError, setHasLoadError] = useState(false);
  const literaryNotes = useSelector(state => state.projectNotes.literaryNotes);
  const dispatch = useDispatch();

  // Defensive array coercion — covers the brief first-render window before
  // our fetchData useEffect runs, in case redux already holds a non-array
  // from a prior failed call.
  const safeLiteraryNotes = Array.isArray(literaryNotes) ? literaryNotes : [];
  const focusedNote = safeLiteraryNotes[focused] || {};

  const toggleAdd = () => {
    setModalOpen(!isModalOpen);
    setIsEditing(false);
    setNote({ internal: true });
  };

  const archiveNote = async projectNote => {
    projectNote.archived = !projectNote.archived;
    await dispatch(updateProjectNote(projectNote._id, projectNote));
    await dispatch(loadLiteraryNotes(project._id));
  };

  const fetchData = useCallback(async () => {
    if (!project?._id) return;
    setHasLoadError(false);
    try {
      const result = await dispatch(loadLiteraryNotes(project._id));
      if (!result?.ok || !Array.isArray(result?.body)) {
        setHasLoadError(true);
      }
    } catch (err) {
      console.error('Error loading literary notes:', err);
      setHasLoadError(true);
    }
  }, [dispatch, project?._id]);

  useEffect(() => {
    fetchData();
    // retryToken is in deps so the page-level Retry (from the parent's
    // SectionErrorBanner) also re-fetches this view.
  }, [fetchData, retryToken]);

  // Case 2 inline fallback — matches the shape of the route-level
  // sectionFallback helper, so a view-level re-fetch failure after a
  // successful parent Phase 2 never leaves the tab blank or crashing.
  if (hasLoadError) {
    return (
      <SectionError
        title="Couldn't load Literary Notes"
        onRetry={fetchData}
        isRetrying={isRetrying}
      />
    );
  }

  const visibleNotes = showArchived
    ? safeLiteraryNotes
    : safeLiteraryNotes.filter(n => !n.archived);
  if (!project?._id && visibleNotes.length === 0) {
    notify.show('Unable to load content, Please try later!', 'custom', 2000, {
      background: '#000000',
      text: '#FFFFFF',
    });
  }

  return (
    <React.Fragment>
      <NoteTableActionHeader
        isModalOpen={isModalOpen}
        toggleAdd={toggleAdd}
        showArchived={showArchived}
        onShowArchiveChange={() => setShowArchived(!showArchived)}
      />

      {isModalOpen && (
        <div className={classes.addContainer}>
          <ProjectNoteForm
            isEdit={isEdit}
            project={project}
            note={note}
            noteType="Literary"
            onDone={() => {
              setModalOpen(false);
              dispatch(loadLiteraryNotes(project._id));
            }}
          />
        </div>
      )}
      <table id="noteTable" className={classes.table}>
        <thead>
          <tr className={classes.tr}>
            {HEADERS.map((header, index) => {
              return (
                <th key={index} className={classes.thText}>
                  {header}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {!project?._id ? (
            <React.Fragment>
              <tr className={classes.td}>
                {Array.from(Array(7).keys(7)).map(item => {
                  return (
                    <td key={item} className="px-2">
                      <DataTableLoader />
                    </td>
                  );
                })}
              </tr>
            </React.Fragment>
          ) : (
            <React.Fragment>
              {visibleNotes
                .sort((a, b) => {
                  const aCreated = a.created_date ? 1 : 0;
                  const bCreated = b.created_date ? 1 : 0;
                  if (aCreated === bCreated) {
                    return Date.parse(b.created_date) - Date.parse(a.created_date);
                  } else {
                    return bCreated - aCreated;
                  }
                })
                .map((note, index) => {
                  const isFocused = focusedNote._id === note._id;
                  return (
                    <Note
                      dispatch={dispatch}
                      showInternalColumn
                      note={note}
                      isEdit={isEdit}
                      index={index}
                      key={index}
                      onEditItem={async projectNote => {
                        setNote(projectNote);
                        setModalOpen(false); // force load person if add already open
                        setModalOpen(true);
                        setIsEditing(true);
                      }}
                      onArchiveNote={archiveNote}
                      onDeleteItem={projectNote => {
                        dispatch(removeProjectNote(projectNote._id));
                        dispatch(loadLiteraryNotes(project._id));
                      }}
                      isFocused={isFocused}
                      setFocused={index => {
                        setFocused(index);
                      }}
                    />
                  );
                })}
            </React.Fragment>
          )}
        </tbody>
      </table>
    </React.Fragment>
  );
};

const styles = {
  addContainer: {
    width: '100%',
    maxWidth: '1000px',
    padding: '25px 10px 0 10px',
    margin: '0 auto',
    background: 'white',
    border: '1px black solid',
  },
  tableActionHeaderContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tr: {
    color: colors.contentColor,
    fontSize: 12,
    textTransform: 'uppercase',
  },
  thText: {
    textAlign: 'left',
    padding: 10,
  },
  td: {
    '&:hover': {
      borderBottom: `1px solid ${colors.border}`,
      backgroundColor: colors.focusBackground,
    },
    backgroundColor: colors.contentBackground,
    border: `1px solid ${colors.background}`,
    borderTopColor: colors.border,
    borderBottom: 0,
    fontWeight: 100,
    fontSize: '11px',
  },
};
export default withStyles(styles)(LiteraryNotes);
